import { useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import Breadcrumb from "@/components/feature/Breadcrumb";
import FavoriteButton from "@/components/feature/FavoriteButton";
import MediaCard from "@/components/feature/MediaCard";
import NewsListItem from "@/components/feature/NewsListItem";
import ShareBar from "@/components/feature/ShareBar";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import { findDetail, formatDate, getRelated, kindLabel } from "@/lib/content";
import type { Movie, TvShow } from "@/types/media";

function NotFoundBlock() {
  return (
    <div className="flex w-full flex-col items-center justify-center px-4 py-24 text-center md:px-6">
      <span className="flex h-20 w-20 items-center justify-center rounded-full bg-background-200/70 text-4xl text-foreground-500">
        <i className="ri-file-unknow-line" />
      </span>
      <h1 className="mt-6 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">内容未找到</h1>
      <p className="mt-3 max-w-md text-sm leading-7 text-foreground-600">
        你访问的影视或资讯可能已被移除，或链接有误。可以返回首页继续浏览其他内容。
      </p>
      <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
        <Link
          to="/"
          className="flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-label text-background-50 transition-colors duration-200 hover:bg-primary-600"
        >
          <i className="ri-home-4-line" />
          返回首页
        </Link>
        <Link
          to="/movies"
          className="flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300/70 px-6 py-3 text-sm font-label text-foreground-800 transition-colors duration-200 hover:border-primary-500/60 hover:text-primary-400"
        >
          浏览电影
        </Link>
      </div>
    </div>
  );
}

export default function Detail() {
  const { id = "" } = useParams();
  const detail = useMemo(() => findDetail(id), [id]);
  const related = useMemo(() => getRelated(id, 6), [id]);

  const media: Movie | TvShow | undefined = detail?.movie ?? detail?.tvshow;
  const metaTitle = detail?.news?.title ?? media?.title ?? "内容未找到";
  const metaDescription =
    detail?.news?.summary ??
    media?.summary ??
    "该内容可能已被移除或链接有误，请返回首页浏览其他影视作品。";
  const metaKeywords = media
    ? [media.title, ...media.genres, media.region, kindLabel(detail?.kind ?? "movie")].join(",")
    : detail?.news
      ? [detail.news.title, detail.news.source, "影视资讯", "夜无疆"].join(",")
      : "夜无疆影视大全";

  useDocumentMeta({
    title: `${metaTitle} | 夜无疆影视详情`,
    description: metaDescription,
    keywords: metaKeywords,
  });

  if (!detail) {
    return (
      <div className="w-full animate-fadeIn">
        <NotFoundBlock />
      </div>
    );
  }

  if (detail.news) {
    const article = detail.news;
    const relatedNews = related.filter((item) => item.kind === "news");

    return (
      <div className="w-full animate-fadeIn">
        <div className="border-b border-background-200/70 bg-background-100/40 px-4 py-6 md:px-6 lg:px-10">
          <Breadcrumb
            items={[{ label: "首页", href: "/" }, { label: "影视资讯", href: "/news" }, { label: article.title }]}
          />
        </div>

        <article className="mx-auto w-full max-w-5xl px-4 py-8 md:px-6 lg:py-12">
          <h1 className="font-heading text-2xl font-extrabold leading-snug text-foreground-950 md:text-4xl">
            {article.title}
          </h1>
          <div className="mt-4 flex flex-wrap items-center gap-4 text-sm text-foreground-500">
            <span className="flex items-center gap-1">
              <i className="ri-calendar-line" />
              {formatDate(article.publishDate)}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-price-tag-3-line" />
              {article.source}
            </span>
          </div>

          <div className="mt-6 aspect-[16/9] w-full overflow-hidden rounded-lg border border-background-200/70 bg-background-200">
            <img
              src={article.cover}
              alt={article.title}
              title={`${article.title} - 夜无疆影视资讯`}
              className="h-full w-full object-cover object-top"
            />
          </div>

          <div className="mt-8 space-y-5">
            {article.content.map((paragraph, index) => (
              <p key={index} className="text-sm leading-8 text-foreground-700 md:text-base">
                {paragraph}
              </p>
            ))}
          </div>

          <div className="mt-8 flex flex-wrap items-center justify-between gap-4 border-t border-background-200/70 pt-6">
            <FavoriteButton id={article.id} />
            <ShareBar title={article.title} />
          </div>
        </article>

        {relatedNews.length > 0 && (
          <section className="w-full border-t border-background-200/70 bg-background-100/40 px-4 py-10 md:px-6 lg:px-10 lg:py-12">
            <h2 className="mb-6 font-heading text-xl font-bold text-foreground-950 md:text-2xl">相关资讯</h2>
            <div className="space-y-4">
              {relatedNews.map((item) => (
                <NewsListItem
                  key={item.id}
                  id={item.id}
                  title={item.title}
                  publishDate={item.tags[0]}
                  source={item.tags[1] ?? "夜无疆影视资讯"}
                  summary={item.summary}
                  cover={item.cover}
                />
              ))}
            </div>
          </section>
        )}
      </div>
    );
  }

  if (!media) {
    return (
      <div className="w-full animate-fadeIn">
        <NotFoundBlock />
      </div>
    );
  }

  const isMovie = detail.kind === "movie";
  const categoryLabel = kindLabel(detail.kind);
  const categoryPath = isMovie ? "/movies" : "/tvshows";
  const tvshow = detail.tvshow;

  return (
    <div className="w-full animate-fadeIn">
      <div className="border-b border-background-200/70 bg-background-100/40 px-4 py-6 md:px-6 lg:px-10">
        <Breadcrumb
          items={[
            { label: "首页", href: "/" },
            { label: categoryLabel, href: categoryPath },
            { label: media.title },
          ]}
        />
      </div>

      <div className="w-full px-4 py-8 md:px-6 lg:px-10 lg:py-12">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-[320px_1fr] lg:gap-12">
          <div className="mx-auto w-56 sm:w-64 lg:mx-0 lg:w-full">
            <div className="aspect-[2/3] w-full overflow-hidden rounded-lg border border-background-200/70 bg-background-200">
              <img
                src={media.cover}
                alt={`${media.title} 海报`}
                title={`${media.title} - 夜无疆影视详情`}
                className="h-full w-full object-cover object-top"
              />
            </div>
          </div>

          <div>
            <span className="inline-flex items-center gap-2 rounded-full bg-primary-500/15 px-3 py-1 text-xs font-label text-primary-400">
              {categoryLabel}
            </span>
            <h1 className="mt-3 font-heading text-3xl font-extrabold leading-tight text-foreground-950 md:text-4xl">
              {media.title}
            </h1>

            <div className="mt-4 flex flex-wrap items-center gap-4 text-sm text-foreground-700">
              <span className="flex items-center gap-1 text-accent-400">
                <i className="ri-star-fill" />
                {media.rating.toFixed(1)}
              </span>
              <span>{media.year}</span>
              <span>{media.region}</span>
              {tvshow && (
                <>
                  <span>{tvshow.episodes} 集</span>
                  <span className="rounded-md bg-secondary-100 px-2 py-0.5 text-xs text-secondary-950">
                    {tvshow.status}
                  </span>
                </>
              )}
            </div>

            <div className="mt-4 flex flex-wrap items-center gap-2">
              {media.genres.map((genre) => (
                <span key={genre} className="rounded-md bg-background-200/70 px-3 py-1 text-xs text-foreground-700">
                  {genre}
                </span>
              ))}
            </div>

            <p className="mt-6 text-sm font-label text-primary-400">剧情简介</p>
            <p className="mt-2 text-sm leading-8 text-foreground-700 md:text-base">{media.synopsis}</p>

            <dl className="mt-8 grid grid-cols-1 gap-x-8 gap-y-4 sm:grid-cols-2">
              <div className="flex gap-3">
                <dt className="w-20 shrink-0 text-sm text-foreground-500">类型</dt>
                <dd className="text-sm text-foreground-800">{media.genres.join(" / ")}</dd>
              </div>
              <div className="flex gap-3">
                <dt className="w-20 shrink-0 text-sm text-foreground-500">地区</dt>
                <dd className="text-sm text-foreground-800">{media.region}</dd>
              </div>
              <div className="flex gap-3">
                <dt className="w-20 shrink-0 text-sm text-foreground-500">
                  {isMovie ? "上映年份" : "首播年份"}
                </dt>
                <dd className="text-sm text-foreground-800">{media.year}</dd>
              </div>
              <div className="flex gap-3">
                <dt className="w-20 shrink-0 text-sm text-foreground-500">导演</dt>
                <dd className="text-sm text-foreground-800">{media.director}</dd>
              </div>
              <div className="flex gap-3 sm:col-span-2">
                <dt className="w-20 shrink-0 text-sm text-foreground-500">主演</dt>
                <dd className="text-sm text-foreground-800">{media.cast.join(" / ")}</dd>
              </div>
            </dl>

            <div className="mt-8 flex flex-wrap items-center justify-between gap-4 border-t border-background-200/70 pt-6">
              <FavoriteButton id={media.id} />
              <ShareBar title={media.title} />
            </div>
          </div>
        </div>
      </div>

      {related.length > 0 && (
        <section className="w-full border-t border-background-200/70 bg-background-100/40 px-4 py-10 md:px-6 lg:px-10 lg:py-12">
          <div className="mb-6 flex items-center justify-between">
            <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
              {isMovie ? "相关电影推荐" : "相关剧集推荐"}
            </h2>
            <Link
              to={categoryPath}
              className="flex items-center gap-1 whitespace-nowrap text-sm font-label text-foreground-600 transition-colors duration-200 hover:text-primary-400"
            >
              更多{categoryLabel}
              <i className="ri-arrow-right-line" />
            </Link>
          </div>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-5 lg:grid-cols-4">
            {related.map((item) => (
              <MediaCard
                key={item.id}
                id={item.id}
                title={item.title}
                year={item.year}
                cover={item.cover}
                summary={item.summary}
                tags={item.tags}
              />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}